/** @typedef {'weak' | 'medium' | 'strong' | 'excellent'} InputQualityTier */

export const INPUT_QUALITY_TIERS = /** @type {const} */ (['weak', 'medium', 'strong', 'excellent'])

export const INPUT_QUALITY_TIER_INDEX = Object.freeze({
  weak: 0,
  medium: 1,
  strong: 2,
  excellent: 3,
})

/** @type {Readonly<Record<InputQualityTier, string>>} */
export const INPUT_QUALITY_HINTS = Object.freeze({
  weak: 'Add more context so attendees know what this meeting is about.',
  medium: 'Good start — a bit more specificity will help on calendars and invites.',
  strong: 'Clear and purposeful — teammates will know what to expect.',
  excellent: 'Highly descriptive — great for search, reminders, and async prep.',
})

/** @type {Readonly<Record<InputQualityTier, string>>} */
export const AGENDA_QUALITY_HINTS = Object.freeze({
  weak: 'Add topic titles and supporting notes so everyone knows what to prepare.',
  medium: 'Topics are sketched — add bullet notes or split long meetings into multiple items.',
  strong: 'Solid agenda — owners and outcomes in the notes will make this even sharper.',
  excellent: 'Thorough agenda — clear topics, context, and room to run the meeting well.',
})

const WELL_KNOWN_MEETING_TERMS = new Set([
  'standup',
  'stand-up',
  'scrum',
  'retro',
  'retrospective',
  'kickoff',
  'kick-off',
  'sync',
  'huddle',
  'allhands',
  'all-hands',
  '1on1',
  '1:1',
  'oneonone',
  'one-on-one',
  'checkpoint',
  'debrief',
  'planning',
  'grooming',
  'backlog',
  'demo',
  'interview',
  'onboarding',
  'warroom',
  'war-room',
  'triage',
  'postmortem',
  'post-mortem',
  'brainstorm',
  'workshop',
  'offsite',
])

const GENERIC_MEETING_WORDS = new Set([
  'meeting',
  'call',
  'chat',
  'discussion',
  'talk',
  'session',
  'catchup',
  'catch-up',
  'update',
  'checkin',
  'check-in',
  'syncup',
])

const ACTION_VERB_PATTERN =
  /\b(present|review|discuss|decide|approve|align|demo|walkthrough|walk-through|feedback|plan|finalize|ship|release|blockers?|prep|prepare|decide|determine|share|update)\b/i

const OWNER_PATTERN =
  /\b([A-Z][a-z]+|[a-z]+)\s+to\s+[a-z]/i

/**
 * @param {string} raw
 * @returns {string[]}
 */
function tokenizeWords(raw) {
  return String(raw ?? '')
    .trim()
    .split(/[\s,;/|]+/)
    .map((w) => w.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ''))
    .filter(Boolean)
}

/**
 * @param {string} word
 */
function normalizeToken(word) {
  return word.toLowerCase().replace(/['']/g, "'")
}

/**
 * @param {string} text
 */
function isMostlySymbols(text) {
  const trimmed = text.trim()
  if (!trimmed) return true
  const letters = (trimmed.match(/[a-zA-Z0-9]/g) || []).length
  return letters === 0
}

/**
 * @param {string} word
 */
function vowelRatio(word) {
  const letters = word.replace(/[^a-zA-Z]/g, '')
  if (!letters) return 0
  const vowels = (letters.match(/[aeiouAEIOU]/g) || []).length
  return vowels / letters.length
}

/**
 * @param {string} word
 */
function looksLikeKeyboardMash(word) {
  const w = word.replace(/[^a-zA-Z]/g, '')
  if (w.length < 4) return false
  if (/(.)\1{2,}/.test(w)) return true
  if (vowelRatio(w) < 0.12 && w.length >= 5) return true
  if (/^[bcdfghjklmnpqrstvwxyz]{5,}$/i.test(w)) return true
  const unique = new Set(w.toLowerCase().split('')).size
  if (w.length >= 6 && unique <= 3) return true
  return false
}

/**
 * @param {string} text
 */
function isNonsensicalText(text) {
  const trimmed = text.trim()
  if (!trimmed) return true
  if (isMostlySymbols(trimmed)) return true

  const words = tokenizeWords(trimmed)
  if (words.length === 0) return true

  const mashCount = words.filter((w) => looksLikeKeyboardMash(w)).length
  if (words.length === 1 && mashCount === 1) return true
  if (words.length > 1 && mashCount / words.length >= 0.5) return true

  const joined = words.join('').toLowerCase()
  if (joined.length >= 8 && vowelRatio(joined) < 0.1) return true

  return false
}

/**
 * @param {string} word
 */
function isWellKnownMeetingTerm(word) {
  const n = normalizeToken(word)
  return WELL_KNOWN_MEETING_TERMS.has(n)
}

/**
 * @param {string} word
 */
function isGenericMeetingWord(word) {
  return GENERIC_MEETING_WORDS.has(normalizeToken(word))
}

/**
 * @param {number} score
 * @returns {InputQualityTier}
 */
function tierFromScore(score) {
  if (score < 25) return 'weak'
  if (score < 50) return 'medium'
  if (score < 75) return 'strong'
  return 'excellent'
}

/**
 * @param {InputQualityTier} tier
 * @returns {number}
 */
function scoreFromTier(tier) {
  const map = { weak: 12, medium: 37, strong: 62, excellent: 88 }
  return map[tier] ?? 0
}

/**
 * Score meeting title / topic title text (0–100).
 * @param {string} text
 * @returns {{ score: number, tier: InputQualityTier }}
 */
export function scoreTitleTextQuality(text) {
  const trimmed = String(text ?? '').trim()
  if (!trimmed || isNonsensicalText(trimmed)) {
    return { score: 0, tier: 'weak' }
  }

  const words = tokenizeWords(trimmed)
  const wordCount = words.length
  const charCount = trimmed.length

  if (wordCount === 1) {
    const token = normalizeToken(words[0])
    if (isWellKnownMeetingTerm(token)) {
      return { score: 42, tier: 'medium' }
    }
    if (isGenericMeetingWord(token) || token.length < 5) {
      return { score: 15, tier: 'weak' }
    }
    if (looksLikeKeyboardMash(words[0])) {
      return { score: 0, tier: 'weak' }
    }
    return { score: 28, tier: 'weak' }
  }

  let score = 20

  if (wordCount === 2) score += 18
  else if (wordCount <= 4) score += 32
  else if (wordCount <= 6) score += 48
  else score += 58

  if (charCount >= 28) score += 8
  if (charCount >= 45) score += 10
  if (charCount >= 65) score += 8

  const hasNumber = /\d/.test(trimmed)
  const hasQuarter = /\bq[1-4]\b/i.test(trimmed)
  const capitalizedWords = words.filter((w) => /^[A-Z]/.test(w) && w.length > 1).length
  const longWords = words.filter((w) => w.length >= 6).length

  if (hasNumber) score += 4
  if (hasQuarter) score += 6
  if (capitalizedWords >= 2) score += 8
  if (longWords >= 2) score += 10
  if (wordCount >= 5 && charCount >= 40) score += 12

  const uniqueRatio = new Set(words.map((w) => normalizeToken(w))).size / wordCount
  if (uniqueRatio < 0.5 && wordCount > 2) score -= 12

  // Short informal phrases (e.g. "tim and raghu catch up") stay medium unless they include specifics.
  if (wordCount <= 5 && charCount < 42 && !hasQuarter && !hasNumber && capitalizedWords < 2 && longWords < 2) {
    score = Math.min(score, 49)
  }

  score = Math.max(0, Math.min(100, score))
  return { score, tier: tierFromScore(score) }
}

/**
 * @param {string} details
 * @returns {{ score: number, tier: InputQualityTier, bulletCount: number }}
 */
export function scoreAgendaBodyQuality(details) {
  const trimmed = String(details ?? '').trim()
  if (!trimmed) {
    return { score: 0, tier: 'weak', bulletCount: 0 }
  }

  const lines = trimmed
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean)

  const bulletLines = lines.filter((l) => /^[-*•]\s+/.test(l) || /^\d+[.)]\s+/.test(l))
  const bulletCount = bulletLines.length
  const allWords = tokenizeWords(trimmed)
  const wordCount = allWords.length

  let score = 12

  if (wordCount >= 6) score += 12
  if (wordCount >= 15) score += 14
  if (wordCount >= 30) score += 10

  if (bulletCount >= 1) score += 16
  if (bulletCount >= 2) score += 18
  if (bulletCount >= 3) score += 8

  const hasActions = bulletLines.some((l) => ACTION_VERB_PATTERN.test(l)) || ACTION_VERB_PATTERN.test(trimmed)
  const hasOwners =
    bulletLines.some((l) => OWNER_PATTERN.test(l)) ||
    /\b[A-Z][a-z]+\s+to\s+/i.test(trimmed)

  if (hasActions) score += 12
  if (hasOwners) score += 10
  if (hasActions && hasOwners && bulletCount >= 2) score += 12

  if (lines.length >= 2 && bulletCount === 0) score += 6

  score = Math.max(0, Math.min(100, score))
  return { score, tier: tierFromScore(score), bulletCount }
}

/**
 * @param {string} details
 */
function hasSubstantiveAgendaBody(details) {
  const trimmed = String(details ?? '').trim()
  if (!trimmed) return false
  if (/^[-*•]\s+/m.test(trimmed)) return true
  return tokenizeWords(trimmed).length >= 8
}

/**
 * Holistic agenda quality for meeting builder step 8 (all topics + notes + duration).
 * @param {Array<{ title?: string, details?: string }>} agendaItems
 * @param {number} meetingDurationMinutes
 * @returns {{ score: number, tier: InputQualityTier }}
 */
export function scoreAgendaOverallQuality(agendaItems, meetingDurationMinutes) {
  const items = (agendaItems ?? [])
    .map((item) => ({
      title: String(item?.title ?? '').trim(),
      details: String(item?.details ?? '').trim(),
    }))
    .filter((item) => item.title)

  if (items.length === 0) {
    return { score: 0, tier: 'weak' }
  }

  const duration =
    typeof meetingDurationMinutes === 'number' && Number.isFinite(meetingDurationMinutes) && meetingDurationMinutes > 0
      ? meetingDurationMinutes
      : 30

  const itemCount = items.length
  const titleScores = items.map((item) => scoreTitleTextQuality(item.title).score)
  const avgTitleScore = titleScores.reduce((sum, s) => sum + s, 0) / itemCount
  const minTitleScore = Math.min(...titleScores)
  const titleComponent =
    itemCount === 1 ? avgTitleScore : Math.round(avgTitleScore * 0.55 + minTitleScore * 0.45)

  const combinedDetails = items.map((item) => item.details).filter(Boolean).join('\n\n')
  const bodyQ = scoreAgendaBodyQuality(combinedDetails)
  const substantiveBodyCount = items.filter((item) => hasSubstantiveAgendaBody(item.details)).length
  const bodyCoverage = substantiveBodyCount / itemCount

  let score
  if (!combinedDetails) {
    score = Math.min(titleComponent, scoreFromTier('medium') - 2)
  } else {
    const bodyWeight = itemCount === 1 ? 0.62 : 0.58
    const coverageFactor = itemCount === 1 ? 1 : 0.5 + bodyCoverage * 0.5
    const bodyContribution = bodyQ.score * coverageFactor
    score = Math.round(titleComponent * (1 - bodyWeight) + bodyContribution * bodyWeight)

    if (itemCount > 1 && bodyCoverage < 1) {
      score -= Math.round((1 - bodyCoverage) * 24)
    }
  }

  const strongTitleCount = titleScores.filter((s) => s >= 50).length
  const excellentTitleCount = titleScores.filter((s) => s >= 75).length

  if (duration <= 30) {
    if (itemCount === 1 && (bodyQ.score >= 50 || titleComponent >= 50)) {
      score = Math.min(100, score + 4)
    }
  } else if (duration <= 45) {
    if (itemCount === 1 && titleComponent < 50 && bodyQ.score < 40) {
      score -= 8
    } else if (itemCount >= 2) {
      score = Math.min(100, score + 4)
    }
  } else if (itemCount === 1) {
    if (bodyQ.bulletCount >= 2 && bodyQ.score >= 50 && titleComponent >= 50) {
      score -= 4
    } else if (titleComponent >= 50 || bodyQ.score >= 50) {
      score -= 10
    } else {
      score -= 18
    }
  } else if (itemCount === 2 && duration >= 60) {
    score = Math.min(100, score + 3)
  } else if (itemCount >= 3) {
    score = Math.min(100, score + 6)
  }

  if (!combinedDetails && excellentTitleCount > 0 && itemCount >= 1) {
    score = Math.min(score, scoreFromTier('medium'))
  }

  if (score >= 75) {
    const richCombinedBody =
      bodyQ.bulletCount >= 3 || tokenizeWords(combinedDetails).length >= 45
    const titlesHoldUp = itemCount === 1 ? titleComponent >= 50 : strongTitleCount === itemCount
    if (!richCombinedBody || !titlesHoldUp) {
      score = Math.min(score, 74)
    }
  }

  score = Math.max(0, Math.min(100, Math.round(score)))
  return { score, tier: tierFromScore(score) }
}
